import type { WidgetDisplayType } from "../types";

export interface WidgetTemplateFallback {
	template: string;
	label: string;
	description: string;
	display_type: WidgetDisplayType;
	version: number;
	preview: React.ReactNode;
}

function PreviewFrame({ children }: { children: React.ReactNode }) {
	return <div className="h-28 w-full rounded-lg border border-gray-200 bg-gray-50 p-3 flex items-center justify-center">{children}</div>;
}

function MiniCardPreview() {
	return (
		<PreviewFrame>
			<div className="w-full rounded-md bg-white border border-gray-200 p-2">
				<div className="h-2 w-12 bg-gray-200 rounded mb-2" />
				<div className="h-2 w-20 bg-gray-200 rounded mb-2" />
				<div className="h-2 w-16 bg-gray-200 rounded" />
			</div>
		</PreviewFrame>
	);
}

function MiniListPreview() {
	return (
		<PreviewFrame>
			<div className="w-full space-y-2">
				{[1, 2].map((item) => (
					<div key={item} className="h-4 rounded bg-white border border-gray-200" />
				))}
			</div>
		</PreviewFrame>
	);
}

function MiniCarouselPreview() {
	return (
		<PreviewFrame>
			<div className="w-full h-16 rounded bg-gradient-to-r from-purple-500 to-indigo-500 opacity-80" />
		</PreviewFrame>
	);
}

function MiniBadgePreview() {
	return (
		<PreviewFrame>
			<div className="w-14 h-14 rounded-xl bg-gray-900 text-white flex items-center justify-center text-lg font-semibold">4.9</div>
		</PreviewFrame>
	);
}

function MiniAvatarStackPreview() {
	return (
		<PreviewFrame>
			<div className="flex items-center justify-center">
				{[1, 2, 3, 4, 5].map((item) => (
					<div
						key={item}
						className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 border-2 border-white"
						style={{ marginLeft: item === 1 ? 0 : "-8px" }}
					/>
				))}
				<div className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-[10px] text-gray-500 font-medium" style={{ marginLeft: "-8px" }}>
					+4
				</div>
			</div>
		</PreviewFrame>
	);
}

function MiniMasonryPreview() {
	return (
		<PreviewFrame>
			<div className="w-full grid grid-cols-3 gap-1">
				{[1, 2, 3, 4, 5, 6].map((item) => (
					<div key={item} className={`rounded bg-white border border-gray-200 ${item % 2 === 0 ? "h-6" : "h-4"}`} />
				))}
			</div>
		</PreviewFrame>
	);
}

export const WIDGET_TEMPLATE_FALLBACKS: WidgetTemplateFallback[] = [
	{
		template: "classic-card",
		label: "Testimonial Card",
		description: "Showcase a single review in a compact card.",
		display_type: "single",
		version: 1,
		preview: <MiniCardPreview />,
	},
	{
		template: "classic-list",
		label: "Testimonial List",
		description: "Display a vertical list of reviews.",
		display_type: "collection",
		version: 1,
		preview: <MiniListPreview />,
	},
	{
		template: "classic-carousel",
		label: "Testimonial Carousel",
		description: "Cycle through reviews in a carousel.",
		display_type: "collection",
		version: 1,
		preview: <MiniCarouselPreview />,
	},
	{
		template: "classic-rating-badge",
		label: "Rating Badge",
		description: "Highlight your average rating and count.",
		display_type: "aggregation",
		version: 1,
		preview: <MiniBadgePreview />,
	},
	{
		template: "classic-masonry-grid",
		label: "Masonry",
		description: "A responsive masonry grid layout for displaying reviews.",
		display_type: "collection",
		version: 1,
		preview: <MiniMasonryPreview />,
	},
	{
		template: "classic-avatar-stack",
		label: "Avatar Stack",
		description: "An interactive avatar stack with review popovers on click.",
		display_type: "collection",
		version: 1,
		preview: <MiniAvatarStackPreview />,
	},
];

export function getFallbackPreview(template: string): React.ReactNode | null {
	const fallback = WIDGET_TEMPLATE_FALLBACKS.find((item) => item.template === template);
	return fallback?.preview ?? null;
}
